import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type {
  LabelFilter,
  QaReviewCounts, QaReviewedItem, QaReviewFilter, QaVerdictValue, ReprocessMethod,
  ReviewLabel, RunEstimate,
} from '../api/types';
import {
  HUMAN_VERDICT_FILTERS, UNLABELLED, emptyLabelFilter, isLabelFilterEmpty,
} from '../api/types';
import { LabelPicker } from './LabelPicker';
import { LabelFilterMenu } from './LabelFilterMenu';

// The label to apply next, remembered across sessions so resuming a review pass needs
// no setup. Per store: label vocabularies are per-store (the table is), so carrying
// one store's label into another would offer a label that filters to nothing there.
const LABEL_KEY = 'ff.qa.activeLabel';
const readStoredLabel = (storeId: string): string => {
  try {
    return window.localStorage.getItem(`${LABEL_KEY}.${storeId}`) ?? '';
  } catch {
    return '';   // private mode / storage disabled — degrade to "pick one each time"
  }
};
const writeStoredLabel = (storeId: string, label: string): void => {
  try {
    window.localStorage.setItem(`${LABEL_KEY}.${storeId}`, label);
  } catch { /* non-fatal: the label still applies, it just won't be remembered */ }
};
import { useStoreSlice } from '../store/storeRegistry';
import { QaReviewCard } from './QaReviewCard';
import { QaBulkRemediationToolbar } from './QaBulkRemediationToolbar';
import { QaZoomModal } from './QaZoomModal';

const PAGE = 60;
// Keep at least this many cards loaded; when bulk actions drop below it we auto-pull
// the next page so the reviewer never hits an empty list + manual refresh.
const AUTO_LOAD_MIN = 12;

const FILTERS: { key: QaReviewFilter; label: string; title?: string }[] = [
  { key: 'needs-fix', label: 'Needs-fix' },
  { key: 'queued', label: 'Queued' },
  { key: 'recut', label: 'Re-cut',
    title: 'Products sent for an un-approve + re-cut — queued, or re-cut and awaiting your review' },
  { key: 'manual', label: 'Manual',
    title: 'Disapproved and left for manual handling — upload a cutout or use a 3rd-party API' },
  { key: 'ai-approved', label: 'AI-approved' },
  { key: 'human-approved', label: 'Human-approved' },
  { key: 'overridden', label: 'Overridden' },
  { key: 'unverified', label: 'Unverified' },
  { key: 'all', label: 'All' },
];

// Bulk endpoints cap a request at 200 product_ids; a selection can exceed that.
const BULK_CHUNK = 200;

function chunk<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

/** After a verdict flip, does the item still belong in the current bucket?
 * (Used to decide whether to drop it from the list or update it in place.) */
function belongsAfterVerdict(f: QaReviewFilter, verdict: QaVerdictValue): boolean {
  if (f === 'approved' || f === 'human-approved') return verdict === 'pass';
  // any operator verdict makes the item human-touched → it leaves the AI-approved bucket
  if (f === 'ai-approved') return false;
  if (f === 'needs-fix' || f === 'disapproved') return verdict === 'fail';
  return true; // queued / overridden / all are unaffected by a verdict flip
}

// Browse filters ported from the retired All-renders tab.
const TIER_OPTIONS = [
  'tier_0', 'tier_1', 'tier_2', 'ben2', 'floodfill', 'passthrough', 'snapedit', 'tier_sam',
];
const SORT_OPTIONS: { value: string; label: string }[] = [
  // Default: grade the commercially important products first (operator's ask).
  { value: 'importance', label: 'Importance (sales + stock)' },
  { value: 'sales', label: 'Top sellers (30d)' },
  { value: 'stock', label: 'Stock value' },
  { value: 'worst', label: 'Worst quality first' },
  { value: 'best', label: 'Best quality first' },
  { value: 'recent', label: 'Most recent' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'id', label: 'Product ID (stable)' },
];

// Value-weighted sorts page on a unique rank cursor (after_rank) rather than
// product_id, so the keyset review flow stays skip/dupe-free while ordering by value.
const VALUE_SORTS = ['importance', 'sales', 'stock'];
const isValueSort = (s: string) => VALUE_SORTS.includes(s);

// Reviewer lanes: a fixed menu of disjoint, auto-balanced splits so two (or
// three) people can grind the same bucket without overlap or gaps. "solo" = 1
// lane = no partition. Encoded as `${lanes}:${lane}`.
const LANE_OPTIONS: { value: string; label: string }[] = [
  { value: '1:0', label: 'Reviewer: solo' },
  { value: '2:0', label: 'Reviewer 1 of 2' },
  { value: '2:1', label: 'Reviewer 2 of 2' },
  { value: '3:0', label: 'Reviewer 1 of 3' },
  { value: '3:1', label: 'Reviewer 2 of 3' },
  { value: '3:2', label: 'Reviewer 3 of 3' },
];

function errDetail(err: unknown): string {
  return err instanceof Error ? err.message : 'unknown error';
}

/** 2026-06-28 reliability: submit PASS verdicts in chunks, returning exactly the
 * ids the server PERSISTED (res.written) vs the ones it didn't. Two hardening
 * properties the old inline loop lacked:
 *   1. a failed chunk (timeout / 5xx) no longer abandons the remaining chunks —
 *      each is independent, so a transient blip can't silently drop a whole
 *      bulk click's tail.
 *   2. a 200 that wrote FEWER rows than requested (a product absent from the
 *      current catalog is JOIN-dropped server-side) is treated as a failure for
 *      that chunk — the caller must not hide unsaved items as "done".
 * Short writes can't be attributed to a specific id, so the whole short chunk is
 * kept visible (safe: a truly-saved item just reappears already-moved on reload). */
async function submitPassVerdictsResilient(
  ids: string[], reason: string, storeId?: string,
): Promise<{ okIds: string[]; failedIds: string[]; error?: string }> {
  const CHUNK = 200;
  const okIds: string[] = [];
  const failedIds: string[] = [];
  let error: string | undefined;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    try {
      const res = await api.submitQaVerdicts(
        slice.map((id) => ({ product_id: id, verdict: 'pass' as QaVerdictValue, reason })),
        storeId,
      );
      if ((res?.written ?? slice.length) >= slice.length) okIds.push(...slice);
      else failedIds.push(...slice);
    } catch (e) {
      failedIds.push(...slice);
      error = errDetail(e);
    }
  }
  return { okIds, failedIds, error };
}

/** Queued-tab run panel (W-H [UX-9] + W-G [CO-8]): tells the operator WHEN
 * queued fixes will render (nightly 03:00) and offers "run now" behind a
 * cost-estimate confirmation — the click has a visible price. */
function QueuedRunPanel({
  storeId,
  queuedCount,
  onToast,
}: {
  storeId: string;
  queuedCount: number | undefined;
  onToast: (m: string) => void;
}) {
  const [estimate, setEstimate] = useState<RunEstimate | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function openConfirm() {
    setBusy(true);
    try {
      setEstimate(await api.runEstimate(storeId));
      setConfirming(true);
    } catch (e) {
      onToast(`Couldn't fetch the cost estimate: ${errDetail(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function runNow() {
    setBusy(true);
    try {
      const res = await api.reprocessQueueRun(storeId);
      onToast(
        `Render started (${res.execution.split('/').pop()}). Queued items re-render in this run.`,
      );
      setConfirming(false);
    } catch (e) {
      onToast(`Couldn't start the run: ${errDetail(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mb-4 rounded-xl border border-ff-sand bg-ff-paper/60 px-4 py-3 text-sm"
      data-testid="queued-run-panel"
    >
      {!confirming ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-ff-ink/80">
            {queuedCount !== undefined ? `${queuedCount} queued — these` : 'Queued items'}{' '}
            re-render automatically on the <strong>next nightly run (03:00 Stockholm)</strong>.
          </span>
          <div className="flex-1" />
          <button
            type="button"
            className="ff-btn-ghost"
            onClick={openConfirm}
            disabled={busy}
            data-testid="queued-run-now"
          >
            {busy ? 'Checking cost…' : "Don't wait — run now…"}
          </button>
        </div>
      ) : (
        <div className="space-y-2" role="alertdialog" aria-label="Confirm paid render run">
          <p className="text-ff-ink/90">
            Running now executes the <strong>whole rendering job</strong> — up to{' '}
            <strong>{estimate?.n_products.toLocaleString()}</strong> products ×{' '}
            ${estimate?.unit_cost_usd} ≈{' '}
            <strong>${estimate?.max_cost_usd.toFixed(2)}</strong> worst-case.
          </p>
          <p className="text-xs text-ff-mute">{estimate?.note}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="ff-btn-ghost"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ff-btn-danger px-4 py-2"
              onClick={runNow}
              disabled={busy}
              data-testid="queued-run-confirm"
            >
              {busy ? 'Starting…' : `Run the job now (≈ up to $${estimate?.max_cost_usd.toFixed(0)})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function QaReviewGallery() {
  const [filter, setFilter] = useState<QaReviewFilter>('needs-fix');
  const [items, setItems] = useState<QaReviewedItem[]>([]);
  const [counts, setCounts] = useState<QaReviewCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [zoom, setZoom] = useState<QaReviewedItem | null>(null);
  const [exhausted, setExhausted] = useState(false);
  // Bulk-remediation selection. Local Set (not the global selectionStore, which
  // belongs to the Cutouts tab) so QA selections never leak across tabs.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tier, setTier] = useState('');   // '' = all tiers (filter ported from All-renders)
  // Default to the value-weighted importance order so the most commercially important
  // products surface at the top of the review flow without any extra clicks.
  const [sort, setSort] = useState('importance');
  // Split-review: auto-balanced reviewer lanes + lock-in filter. lanes=1 = solo.
  const [lanes, setLanes] = useState(1);
  const [lane, setLane] = useState(0);
  // 2026-08-18 defaults: Hide reviewed OFF (across multiple review rounds it hides
  // the very items a later pass exists to re-check); Hide queued ON (a pending
  // override means the cutout is about to change — scoring it now is wasted work).
  const [hideReviewed, setHideReviewed] = useState(false);
  const [hideQueued, setHideQueued] = useState(true);
  const storeId = useStoreSlice((s) => s.selectedStoreId);
  // Review labels: `labelFilter` narrows the view — show any of `include` (plus the
  // UNLABELLED sentinel), minus anything carrying an `exclude`. `activeLabel` is the
  // one the buttons apply.
  const [labels, setLabels] = useState<ReviewLabel[]>([]);
  const [labelFilter, setLabelFilter] = useState<LabelFilter>(emptyLabelFilter);
  const [activeLabel, setActiveLabel] = useState(() => readStoredLabel(storeId));

  // "Hide reviewed" adds `human.product_id IS NULL`, but these buckets are DEFINED by
  // carrying a human verdict — so the flag can only ever empty them. Human-approved
  // used to render "Nothing here." on first load for exactly this reason, which is the
  // bucket the dark-background re-cut workflow starts from. Force it off here.
  const hideReviewedLocked = HUMAN_VERDICT_FILTERS.includes(filter);
  const effectiveHideReviewed = hideReviewed && !hideReviewedLocked;
  // "Hide queued" subtracts pending overrides — the Queued bucket is DEFINED by
  // them, so the flag can only ever empty it. Re-cut stays unlocked on purpose:
  // hiding queued there leaves exactly the landed re-cuts awaiting review.
  const hideQueuedLocked = filter === 'queued';
  const effectiveHideQueued = hideQueued && !hideQueuedLocked;

  // Continuous-review plumbing (Issue 2): items the operator has actioned —
  // filtered out of every load so they never reappear, even on buckets the backend
  // doesn't exclude or before BQ catches up. Keyed PER FILTER and persisted for the
  // whole session (NOT reset on tab switch), so an item handled in one tab stays
  // gone in THAT tab while still showing in other tabs where it now belongs.
  const handledByFilterRef = useRef<Map<QaReviewFilter, Set<string>>>(new Map());
  const handledFor = (f: QaReviewFilter): Set<string> => {
    let s = handledByFilterRef.current.get(f);
    if (!s) { s = new Set(); handledByFilterRef.current.set(f, s); }
    return s;
  };
  // Keyset cursor = last product_id FETCHED (advances monotonically), so auto-load
  // can continue forward even after the visible list is emptied by bulk actions.
  const cursorRef = useRef<string | undefined>(undefined);
  // Parallel cursor for value sorts: the last fetched item's sort_rank (a unique,
  // monotonic int). Used instead of the product_id cursor when sorting by value.
  const rankCursorRef = useRef<number | undefined>(undefined);
  const itemsLenRef = useRef(0);

  // Multi-select + the bulk toolbar are available on every tab (e.g. bulk-queue
  // already-approved cutouts for a fresh re-cut).
  const selectable = true;

  // `load` closes over every browse/review knob (deps), so changing any one
  // re-creates it and the mount effect below re-queries from the start.
  //   append=false → fresh load (replace); append=true → next page (accumulate).
  // A reviewer lane (lanes>1) forces a stable product_id order + keyset paging
  // (after_id) so pages never skip/dupe as items get reviewed; otherwise we honor
  // the chosen browse sort with classic offset paging.
  // Keyset (stable order) drives the review flow: a reviewer lane, an explicit id
  // sort, a value sort (importance/sales/stock), OR hide-reviewed (the default). Only
  // the explicit quality/time browse sorts (worst/best/recent) fall back to offset.
  // Value sorts stay keyset but page on the rank cursor, not product_id.
  const useKeyset = lanes > 1 || sort === 'id' || isValueSort(sort)
    || effectiveHideReviewed || effectiveHideQueued;

  const load = useCallback(
    async (
      f: QaReviewFilter,
      { append = false, afterId, afterRank, offset = 0 }:
        { append?: boolean; afterId?: string; afterRank?: number; offset?: number } = {},
    ) => {
      setLoading(true);
      setLoadError(null);
      const valueSort = isValueSort(sort);
      // derived from the bucket BEING loaded, not the rendered one, so a bucket
      // switch can't send the flag that would empty it (see hideReviewedLocked).
      const hr = hideReviewed && !HUMAN_VERDICT_FILTERS.includes(f);
      const hq = hideQueued && f !== 'queued';
      const ks = lanes > 1 || sort === 'id' || valueSort || hr || hq;
      // A fresh load resets only the keyset cursors (new pagination from the top).
      // The handled-set is PER FILTER and persists across tab switches — it is NOT
      // wiped here, so returning to a tab never re-shows what you already actioned.
      if (!append) { cursorRef.current = undefined; rankCursorRef.current = undefined; }
      try {
        const res = await api.getQaReviewed(f, PAGE, ks ? 0 : offset,
          debouncedSearch || undefined, {
            tier: tier || undefined,
            // value sorts keep their key + page on a rank cursor; other keyset modes
            // pin to the stable product_id order.
            sort: valueSort ? sort : (ks ? 'id' : (sort || undefined)),
            lanes: lanes > 1 ? lanes : undefined,
            lane: lanes > 1 ? lane : undefined,
            afterId: (ks && !valueSort) ? afterId : undefined,
            afterRank: valueSort ? afterRank : undefined,
            hideReviewed: hr || undefined,
            hideQueued: hq || undefined,
            labels: labelFilter.include.length ? labelFilter.include : undefined,
            excludeLabels: labelFilter.exclude.length ? labelFilter.exclude : undefined,
            store: storeId,
          });
        setCounts(res.counts);
        setExhausted(res.items.length < PAGE);
        // Advance both keyset cursors to the last FETCHED item (handled or not), so
        // the next page continues forward regardless of what we hide.
        if (res.items.length) {
          const last = res.items[res.items.length - 1];
          cursorRef.current = last.product_id;
          rankCursorRef.current = last.sort_rank ?? undefined;
        }
        const handled = handledFor(f);
        const fresh = res.items.filter((it) => !handled.has(it.product_id));
        setItems((prev) => (append ? [...prev, ...fresh] : fresh));
      } catch (err) {
        // W-C [UX-7]: an API failure used to render as the "Nothing here."
        // empty state — broken and empty must not look identical.
        setLoadError(errDetail(err));
        if (!append) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [tier, sort, lanes, lane, hideReviewed, hideQueued, debouncedSearch, storeId, labelFilter],
  );

  // Next page: keyset → after the last fetched id (survives an emptied list);
  // else → offset by count. Stable identity so the auto-load effect doesn't loop.
  const loadMore = useCallback(() => {
    if (isValueSort(sort)) return load(filter, { append: true, afterRank: rankCursorRef.current });
    const ks = lanes > 1 || sort === 'id' || effectiveHideReviewed || effectiveHideQueued;
    return load(filter, ks
      ? { append: true, afterId: cursorRef.current }
      : { append: true, offset: itemsLenRef.current });
  }, [load, filter, lanes, sort, effectiveHideReviewed, effectiveHideQueued]);

  // Re-pull just the bucket counts (no list reload, no scroll jump). A failed
  // counts refresh only staleness the chips — surfaced, not thrown.
  const refreshCounts = useCallback(() => {
    // same label scope as the list, or the chips would contradict their own tabs
    api.getQaReviewed(filter, 1, 0, undefined, {
      store: storeId,
      labels: labelFilter.include.length ? labelFilter.include : undefined,
      excludeLabels: labelFilter.exclude.length ? labelFilter.exclude : undefined,
    })
      .then((r) => setCounts(r.counts))
      .catch((err) => setToast(`Couldn't refresh counts: ${errDetail(err)}`));
  }, [filter, storeId, labelFilter]);

  // Label vocabulary for both dropdowns. Refreshed after every apply/remove so a
  // freshly created label immediately becomes filterable.
  const refreshLabels = useCallback(() => {
    api.listReviewLabels(storeId)
      .then((r) => setLabels(r.labels))
      .catch(() => setLabels([]));   // a dropdown failing must not break the page
  }, [storeId]);

  useEffect(() => { refreshLabels(); }, [refreshLabels]);

  // Switching store swaps the label vocabulary, so restore that store's remembered
  // label and drop a filter that means nothing in the new store.
  useEffect(() => {
    setActiveLabel(readStoredLabel(storeId));
    setLabelFilter(emptyLabelFilter());
  }, [storeId]);

  const chooseActiveLabel = useCallback((label: string) => {
    setActiveLabel(label);
    writeStoredLabel(storeId, label);
  }, [storeId]);

  // Remove resolved items from the visible list IN PLACE (preserves scroll +
  // loaded pages) and refresh counts — instead of refetching from offset 0.
  const dropItems = useCallback(
    (ids: string[]) => {
      const handled = handledFor(filter);
      ids.forEach((id) => handled.add(id));   // never re-show a handled item in this tab
      const drop = new Set(ids);
      setItems((prev) => prev.filter((it) => !drop.has(it.product_id)));
      setZoom((z) => (z && drop.has(z.product_id) ? null : z));
      refreshCounts();
    },
    [refreshCounts, filter],
  );

  // Keep a ref of the visible count (so loadMore's identity stays stable).
  useEffect(() => { itemsLenRef.current = items.length; }, [items.length]);

  // Auto-load: when bulk actions drain the list below the buffer, pull the next
  // page forward (keyset) so the reviewer flows continuously instead of hitting an
  // empty list + manual Refresh. Gated on a fetched cursor + !exhausted to converge.
  useEffect(() => {
    if (useKeyset && !loading && !exhausted && cursorRef.current
        && items.length < AUTO_LOAD_MIN) {
      loadMore();
    }
  }, [items.length, loading, exhausted, useKeyset, loadMore]);

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // The handled-set means "I actioned this in the view I'm working in", so it must not
  // survive a change of view. Changing the label filter re-slices every bucket: without
  // this, labelling 60 items under Unlabelled and then filtering TO that label to review
  // them shows an EMPTY list — the server returns them and the client hides them all.
  // Safe to clear wholesale: where the label filter excludes items it does so
  // server-side, so the handled-set is redundant for exactly those items.
  // Declared BEFORE the load effect so it runs first in the same commit.
  // Sorted and signed: the NUL separator can't collide with label text, the +/-
  // prefixes keep "show pass-1" distinct from "hide pass-1", and sorting stops a mere
  // re-tick order from clearing the set for nothing. Written as an escape, not a raw
  // NUL byte — a literal one makes this whole file read as binary to ripgrep.
  const labelFilterKey = [
    ...[...labelFilter.include].sort().map((l) => `+${l}`),
    ...[...labelFilter.exclude].sort().map((l) => `-${l}`),
  ].join('\u0000');
  useEffect(() => {
    handledByFilterRef.current.clear();
  }, [labelFilterKey]);

  useEffect(() => {
    setSelected(new Set()); // changing buckets/search/lane drops any in-flight selection
    load(filter, { append: false }); // load() handles its own errors + reads debouncedSearch
  }, [filter, load]);

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allSelected = items.length > 0 && items.every((it) => selected.has(it.product_id));
  function toggleSelectAll() {
    setSelected(allSelected ? new Set() : new Set(items.map((it) => it.product_id)));
  }

  // "Reprocess as [tier]" — queue a re-render at the chosen model via the
  // reused Cutouts bulk-rerender endpoint. Fire-and-forget: the runner/Job
  // picks the override up on its next execution; we just enqueue.
  async function reprocessSelected(method: ReprocessMethod) {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.bulkEnqueueReprocess(storeId, ids, method);
      setToast(`Queued ${res.count} as ${method} — moved to the Queued tab.`);
      setSelected(new Set());
      dropItems(ids);   // they leave this tab for Queued; no scroll reset
    } catch (e) {
      setToast(`Reprocess failed: ${(e as Error).message}`);
    } finally {
      setBulkBusy(false);
    }
  }

  // "Un-approve + re-cut as [tier]" — the action a plain "Reprocess as" cannot do:
  // it revokes the human-approved pin first, because choose_cutout_tier consults
  // approvals BEFORE overrides and would otherwise ship the pinned cutout unchanged.
  // Products already at the chosen tier come back in diverted_ids: their cutout would
  // be byte-identical, so the server parks them for manual handling instead.
  async function recutSelected(method: ReprocessMethod) {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Un-approve and re-cut ${ids.length} product(s) as ${method}?\n\n` +
      `This removes the human-approved status so the new cut actually takes effect. ` +
      `They re-render on the next job run, then come back here under "Re-cut" for ` +
      `your review.`,
    )) return;
    setBulkBusy(true);
    try {
      let queued = 0;
      let diverted = 0;
      const failed: string[] = [];
      for (const slice of chunk(ids, BULK_CHUNK)) {
        const res = await api.bulkRecut(storeId, slice, method);
        queued += res.recut_count;
        diverted += res.diverted_count;
        failed.push(...res.failed.map((f) => f.product_id));
      }
      setToast(
        `Queued ${queued} as ${method} — they re-cut on the next job run.`
        + (diverted
          ? ` ${diverted} were already ${method}, so a re-cut can't change them — `
            + `parked for manual instead.`
          : '')
        + (failed.length ? ` ${failed.length} couldn't be actioned (not in the catalog).` : ''),
      );
      setSelected(new Set());
      dropItems(ids);   // they leave this tab for Re-cut / Manual; no scroll reset
    } catch (e) {
      setToast(`Un-approve + re-cut failed: ${errDetail(e)}`);
    } finally {
      setBulkBusy(false);
    }
  }

  // "✗ Manual" — disapprove and hand to a human. No re-cut is queued; the live ad
  // keeps rendering until a manual cutout replaces it.
  async function manualParkSelected() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      let ok = 0;
      const problems: string[] = [];
      for (const slice of chunk(ids, BULK_CHUNK)) {
        const res = await api.bulkManualPark(storeId, slice);
        ok += res.ok_count;
        problems.push(...res.failed.map((f) => f.error));
      }
      setToast(
        `Parked ${ok} for manual handling — find them under "Manual".`
        + (problems.length ? ` (${problems[0]})` : ''),
      );
      setSelected(new Set());
      dropItems(ids);
    } catch (e) {
      setToast(`Manual park failed: ${errDetail(e)}`);
    } finally {
      setBulkBusy(false);
    }
  }

  // Apply / remove the active review label. Shared by the bulk toolbar (selection)
  // and the bottom "label all above" button (every loaded item) — same label, so the
  // operator sets it once per pass.
  const applyLabelTo = useCallback(async (ids: string[], mode: 'add' | 'remove') => {
    const label = activeLabel.trim();
    if (!label || ids.length === 0) return;
    setBulkBusy(true);
    try {
      let ok = 0;
      for (const slice of chunk(ids, BULK_CHUNK)) {
        const res = mode === 'add'
          ? await api.applyReviewLabel(storeId, slice, label)
          : await api.removeReviewLabel(storeId, slice, label);
        ok += res.ok_count;
      }
      refreshLabels();
      setSelected(new Set());
      if (mode === 'add') {
        setToast(`Labelled ${ok} as “${label}”.`);
        // Only vanish when the view is actually excluding them; with the filter off
        // (or set to this very label) they still belong on screen, and hiding them
        // would look like the action deleted something. They now carry `label`, so
        // they leave a view that HIDES it — that's the "mark it done and it's gone"
        // pass this feature exists for — or an untriaged-only view they just left.
        if (labelFilter.exclude.includes(label)
            || labelFilter.include.includes(UNLABELLED)) dropItems(ids);
      } else {
        setToast(`Removed “${label}” from ${ok}.`);
        if (labelFilter.include.includes(label)) dropItems(ids);
      }
      refreshCounts();
    } catch (e) {
      setToast(`${mode === 'add' ? 'Labelling' : 'Removing the label'} failed: ${errDetail(e)}`);
    } finally {
      setBulkBusy(false);
    }
  }, [activeLabel, storeId, labelFilter, dropItems, refreshCounts, refreshLabels]);

  // "Send to needs-review (manual/SAM)" — record a manual override so the items
  // move to the Queued tab for a re-cut (or interactive SAM fix).
  async function sendToNeedsReview() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await api.createCutoutOverrides(storeId, {
        product_ids: ids, reason: 'manual', notes: 'qa-disapproved',
      });
      setToast(`Queued ${ids.length} for re-cutout — moved to the Queued tab.`);
      setSelected(new Set());
      dropItems(ids);
    } catch (e) {
      setToast(`Send failed: ${(e as Error).message}`);
    } finally {
      setBulkBusy(false);
    }
  }

  // Bulk "Human approve" — lock in the whole SELECTION as operator-pass, including
  // items already queued for a re-cut (the leftovers "approve all above" skips). An
  // explicit selection is the operator saying "these are fine, keep their cutout".
  async function humanApproveSelected() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { okIds, failedIds, error } = await submitPassVerdictsResilient(
        ids, 'bulk human-approve (selected)', storeId);
      setSelected(new Set(failedIds));        // keep only the unsaved selected, for retry
      if (okIds.length) dropItems(okIds);     // hide ONLY what truly persisted
      setToast(failedIds.length
        ? `Saved ${okIds.length} of ${ids.length}. ${failedIds.length} couldn’t be saved`
          + `${error ? ` (${error})` : ''} — still listed, please retry.`
        : `Human-approved ${okIds.length} as pass — locked in to their cutouts.`);
    } finally {
      setBulkBusy(false);
    }
  }

  async function override(item: QaReviewedItem, verdict: QaVerdictValue) {
    setBusyId(item.product_id);
    try {
      const res = await api.submitQaVerdicts([{ product_id: item.product_id, verdict }], storeId);
      if ((res?.written ?? 1) < 1) {
        // 200 but nothing persisted (product absent from the current catalog) —
        // do NOT hide it as handled; the judgment wasn't saved.
        setToast(`Couldn’t save the ${verdict} verdict for ${item.product_id}: `
          + `it isn’t in the current catalog.`);
        return;
      }
      if (!belongsAfterVerdict(filter, verdict)) {
        // the verdict moved it out of this bucket — drop it in place (keep scroll).
        dropItems([item.product_id]);
      } else {
        // still belongs here — just update the badges in place.
        setItems((prev) =>
          prev.map((it) =>
            it.product_id === item.product_id
              ? { ...it, human_verdict: verdict, effective_verdict: verdict }
              : it,
          ),
        );
        setZoom((z) =>
          z && z.product_id === item.product_id
            ? { ...z, human_verdict: verdict, effective_verdict: verdict }
            : z,
        );
        refreshCounts();
      }
    } catch (err) {
      // W-C [UX-7]: a failed verdict click looked like it worked — the human
      // judgment was silently lost.
      setToast(`Saving the ${verdict} verdict for ${item.product_id} failed: ${errDetail(err)}`);
    } finally {
      setBusyId(null);
    }
  }

  // Loaded items the operator hasn't already verdicted or queued — the targets
  // for "approve all above". (human_verdict==null AND not queued for a re-cut.)
  const approvable = items.filter(
    (it) => it.effective_verdict != null         // AI-prechecked only — never bulk-approve unverified
      && it.human_verdict == null && it.remediation_status !== 'requested',
  );

  // "Human-approve all above": batch operator PASS verdicts over the loaded,
  // not-yet-handled items. A pass verdict is cutout-keyed (survives a design
  // re-render, invalidated by a re-cut), removes the item from review, and keeps
  // its current cutout. Lighter than pinning approvals (no per-URL HEAD checks).
  async function approveAllLoaded() {
    if (approvable.length === 0) {
      setToast('Nothing to approve — every loaded item is already reviewed or queued.');
      return;
    }
    if (!window.confirm(
      `Human-approve ${approvable.length} loaded cutout(s) as pass? They’re locked in to ` +
      `their cutout (kept as-is, hidden from review) and excluded from re-cut runs.`,
    )) return;
    setBulkBusy(true);
    try {
      const ids = approvable.map((t) => t.product_id);
      const { okIds, failedIds, error } = await submitPassVerdictsResilient(
        ids, 'bulk human-approve (reviewed above)', storeId);
      if (okIds.length) dropItems(okIds);     // hide ONLY what truly persisted
      setToast(failedIds.length
        ? `Saved ${okIds.length} of ${ids.length}. ${failedIds.length} couldn’t be saved`
          + `${error ? ` (${error})` : ''} — still listed, please retry.`
        : `Human-approved ${okIds.length} as pass — locked in to their cutouts.`);
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div>
      <div className="ff-toolbar mb-6 flex flex-wrap items-center gap-2">
        <div className="flex w-full items-center gap-2 overflow-x-auto pb-1 xl:w-auto xl:flex-wrap xl:pb-0" role="tablist">
          {FILTERS.map((f) => {
            const active = f.key === filter;
            const n = counts?.[f.key];
            return (
              <button
                key={f.key}
                role="tab"
                aria-selected={active}
                title={f.title}
                onClick={() => setFilter(f.key)}
                className={
                  'whitespace-nowrap px-3 py-1.5 text-xs font-bold uppercase tracking-widish rounded-full border transition-colors ' +
                  (active
                    ? 'border-ff-ink bg-ff-ink text-white'
                    : 'border-ff-line bg-white text-ff-mute hover:border-ff-ink/25 hover:text-ff-ink')
                }
              >
                {f.label}
                {n !== undefined && <span className="ml-1.5 opacity-70">{n}</span>}
              </button>
            );
          })}
        </div>
        <div className="hidden flex-1 xl:block" />
        <select
          aria-label="Filter by cutout tier"
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="ff-input-sm"
        >
          <option value="">All tiers</option>
          {TIER_OPTIONS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          aria-label="Sort order"
          value={useKeyset ? 'id' : sort}
          disabled={useKeyset}
          title={useKeyset
            ? 'Review mode uses a stable Product ID order — turn off "Hide reviewed" / "Hide queued" (and lanes) to sort'
            : undefined}
          onChange={(e) => setSort(e.target.value)}
          className="ff-input-sm disabled:opacity-50"
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <LabelFilterMenu labels={labels} value={labelFilter} onChange={setLabelFilter} />
        <select
          aria-label="Reviewer lane — split the catalog between reviewers"
          value={`${lanes}:${lane}`}
          onChange={(e) => {
            const [ln, l] = e.target.value.split(':').map(Number);
            setLanes(ln);
            setLane(l);
          }}
          className="ff-input-sm"
        >
          {LANE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label
          title={hideReviewedLocked
            ? `This bucket only contains items you've already reviewed, so "Hide reviewed" `
              + `would always empty it — it's off here.`
            : undefined}
          className={
            'flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-full border border-ff-line bg-white '
            + (hideReviewedLocked
              ? 'text-ff-mute/50 cursor-not-allowed'
              : 'text-ff-mute cursor-pointer hover:text-ff-forest hover:border-ff-forest/40')
          }
        >
          <input
            type="checkbox"
            checked={effectiveHideReviewed}
            disabled={hideReviewedLocked}
            onChange={(e) => setHideReviewed(e.target.checked)}
            aria-label="Hide already-reviewed items"
            className="w-4 h-4 accent-ff-orange"
          />
          Hide reviewed
        </label>
        <label
          title={hideQueuedLocked
            ? 'The Queued bucket only contains queued items, so "Hide queued" '
              + 'would always empty it — it\'s off here.'
            : 'Hide products with a re-cut queued — their cutout is about to '
              + 'change, so reviewing them now is wasted work.'}
          className={
            'flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-full border border-ff-line bg-white '
            + (hideQueuedLocked
              ? 'text-ff-mute/50 cursor-not-allowed'
              : 'text-ff-mute cursor-pointer hover:text-ff-forest hover:border-ff-forest/40')
          }
        >
          <input
            type="checkbox"
            checked={effectiveHideQueued}
            disabled={hideQueuedLocked}
            onChange={(e) => setHideQueued(e.target.checked)}
            aria-label="Hide queued items"
            className="w-4 h-4 accent-ff-orange"
          />
          Hide queued
        </label>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search id or title…"
          aria-label="Search by product id or title"
          className="ff-input-sm min-w-44 flex-1 xl:flex-none"
        />
        {/* Remediation status is async/fire-and-forget; there's no live progress,
            so the operator refreshes to pick up status changes from the backend. */}
        <button
          type="button"
          onClick={() => load(filter, { append: false })}
          disabled={loading}
          className="rounded-full border border-ff-line bg-white px-3 py-1.5 text-xs font-bold text-ff-mute transition-colors hover:border-ff-ink/25 hover:text-ff-ink disabled:opacity-50"
        >
          ⟳ Refresh
        </button>
      </div>

      {filter === 'queued' && (
        <QueuedRunPanel
          storeId={storeId}
          queuedCount={counts?.queued}
          onToast={setToast}
        />
      )}

      {selectable && (
        <QaBulkRemediationToolbar
          count={selected.size}
          disabled={bulkBusy}
          onReprocess={reprocessSelected}
          onRecut={recutSelected}
          onSendToNeedsReview={sendToNeedsReview}
          onManualPark={manualParkSelected}
          onHumanApprove={humanApproveSelected}
          onClear={() => setSelected(new Set())}
          labels={labels}
          activeLabel={activeLabel}
          onActiveLabelChange={chooseActiveLabel}
          onApplyLabel={() => applyLabelTo(Array.from(selected), 'add')}
          onRemoveLabel={() => applyLabelTo(Array.from(selected), 'remove')}
        />
      )}

      {toast && (
        <div className="bg-ff-paper border border-ff-sand rounded p-2 text-sm mb-4 flex items-center gap-3">
          <span className="flex-1">{toast}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="text-xs font-mono uppercase text-ff-mute hover:text-ff-forest"
          >
            dismiss
          </button>
        </div>
      )}

      {selectable && items.length > 0 && (
        <div className="flex items-center gap-3 mb-4">
          <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widish text-ff-mute cursor-pointer">
            <input
              type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            aria-label="Select all on page"
            className="w-4 h-4 accent-ff-orange"
          />
            Select all on page
          </label>
        </div>
      )}

      {loadError && !loading ? (
        <div
          className="rounded-2xl border border-ff-danger/30 bg-ff-danger-soft px-8 py-12 text-center"
          role="alert"
          data-testid="qa-load-error"
        >
          <p className="font-display text-lg font-bold text-ff-danger">Couldn't load this list.</p>
          <p className="mt-1 text-sm text-ff-ink/80">{loadError}</p>
          <button
            type="button"
            className="ff-btn-ghost mt-4"
            onClick={() => load(filter, { append: false })}
          >
            Try again
          </button>
        </div>
      ) : items.length === 0 && !loading ? (
        <div className="rounded-2xl border border-dashed border-ff-line bg-white/70 px-8 py-16 text-center">
          <p className="font-display text-lg font-bold text-ff-ink">Nothing here.</p>
          <p className="mt-1 text-sm text-ff-mute">
            No reviewed cutouts match this filter yet.
          </p>
        </div>
      ) : (
        // auto-fill + min card width: columns scale to the screen, cards stay a
        // comfortable size (more per line on wide monitors, never tiny).
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
          {items.map((item) => (
            <QaReviewCard
              key={item.product_id}
              item={item}
              storeId={storeId}
              busy={busyId === item.product_id}
              selectable={selectable}
              selected={selected.has(item.product_id)}
              onToggleSelect={() => toggleSelect(item.product_id)}
              onOverride={(v) => override(item, v)}
              onZoom={() => setZoom(item)}
              onChanged={() => dropItems([item.product_id])}
              onToast={setToast}
            />
          ))}
        </div>
      )}

      <div className="mt-8 flex flex-col items-center gap-3">
        {items.length > 0 && (
          <div
            className="font-mono text-[11px] uppercase tracking-widish text-ff-mute"
            data-testid="review-progress"
          >
            {items.length} loaded
            {' · '}{approvable.length} to review
            {/* bucket total is only accurate when no browse filter narrows it */}
            {counts?.[filter] != null && !tier && !debouncedSearch && (
              <>
                {' · '}{counts[filter]}
                {labelFilter.include.length === 1
                 && labelFilter.include[0] === UNLABELLED
                 && labelFilter.exclude.length === 0
                  ? ' still unlabelled'
                  : isLabelFilterEmpty(labelFilter) ? ` in ${filter}` : ' matching'}
              </>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-center gap-3">
          {items.length > 0 && (
            <button
              type="button"
              className="ff-btn-ghost"
              disabled={bulkBusy || approvable.length === 0}
              onClick={approveAllLoaded}
              data-testid="approve-all-above"
              title="Mark every loaded, not-yet-handled cutout as human-approved (pass)"
            >
              ✓ Human-approve all {approvable.length} above
            </button>
          )}
          {items.length > 0 && (
            <span className="flex items-center gap-2">
              <LabelPicker
                labels={labels}
                value={activeLabel}
                onChange={chooseActiveLabel}
                disabled={bulkBusy}
                data-testid="footer-label-picker"
              />
              <button
                type="button"
                className="ff-btn-ghost"
                disabled={bulkBusy || !activeLabel.trim()}
                onClick={() => applyLabelTo(items.map((i) => i.product_id), 'add')}
                data-testid="label-all-above"
                title="Mark every loaded item as triaged. A progress marker only — it
changes nothing about the products or their cutouts."
              >
                🏷 Label all {items.length} above
              </button>
            </span>
          )}
          {loading ? (
            <span className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
              Loading…
            </span>
          ) : (
            !exhausted && items.length > 0 && (
              <button className="ff-btn-ghost" onClick={loadMore}>
                Load more
              </button>
            )
          )}
        </div>
      </div>

      {zoom && (
        <QaZoomModal
          item={zoom}
          busy={busyId === zoom.product_id}
          onClose={() => setZoom(null)}
          onOverride={(v) => override(zoom, v)}
        />
      )}
    </div>
  );
}
